import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule} from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { combineLatest, filter, firstValueFrom, Subject, take } from 'rxjs';
import { NavItem } from '../../models/nav-item.model';
import { Facility } from '../../models/facility.model';

import { HeaderComponent } from "../../components/header/header.component";
import { FacilityService } from '../../services/facility.service';
import { Customer } from '../../models/customer.model';
import { FacilityYear } from '../../models/facility-year.model';
import { ReportingPeriod } from '../../models/reporting-period.model';
import { CommonDataService } from '../../services/common-data.service';
import { MasterReportingPeriod, MasterReportingPeriodStatus } from '../../models/master-reporting-period.model';
import { SubscriptionStatusService, SubscriptionStatus } from '../../services/subscription-status.service';

@Component({
    selector: 'portal-layout',
    imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    HeaderComponent
],
    templateUrl: './portal.component.html',
    styleUrl: './portal.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class PortalLayoutComponent implements OnDestroy, OnInit
{
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    public title: string = '';

    public currentFacility: Facility | undefined;
    public facilities: Facility[] = [];
    public customers: Customer[] = [];
    public currentCustomer: Customer | undefined;
    
    public currentYear: number = new Date().getFullYear();
    public reportingPeriods: MasterReportingPeriod[] = [];
    public MasterReportingPeriodStatus = MasterReportingPeriodStatus;
    public currentReportingPeriods: ReportingPeriod[] = [];

    get isCurrentYearClosed(): boolean {
        const period = this.reportingPeriods.find(p => p.year === this.currentYear);
        return period?.status === MasterReportingPeriodStatus.Closed;
    }
    public subscriptionStatus: SubscriptionStatus | null = null;
    public isSubscriptionStatusLoading: boolean = true;
    public isOnPurchasePage: boolean = false;
    public isOnCustomerDashboard: boolean = false;
    public isFacilitySwitchLoading: boolean = false;
    public facilityLoadErrorMessage: string | null = null;
    private latestFacilityYear: FacilityYear | null = null;
    private requestedFacilityYear: FacilityYear | null = null;

    get showFacilityLoadErrorOverlay(): boolean {
        return !!this.facilityLoadErrorMessage;
    }

    get showSubscriptionOverlay(): boolean {
        return !this.isFacilitySwitchLoading
            && !this.showFacilityLoadErrorOverlay
            && !this.isSubscriptionStatusLoading
            && !!this.subscriptionStatus
            && !this.subscriptionStatus.isPaid
            && !this.isOnPurchasePage
            && !this.isOnCustomerDashboard;
    }

    get shouldHideContent(): boolean {
        return this.isFacilitySwitchLoading || this.showFacilityLoadErrorOverlay || this.showSubscriptionOverlay;
    }

    get shouldDisableNavigation(): boolean {
        return this.isFacilitySwitchLoading
            || this.showFacilityLoadErrorOverlay
            || this.isSubscriptionStatusLoading
            || (!!this.subscriptionStatus && !this.subscriptionStatus.isPaid);
    }

    public navItems: NavItem[] = [
        { path: '/portal/dashboard', title: 'Customer Dashboard', alwaysEnabled: true },
        { path: '/portal/facility', title: 'Facility',
              children: [
                { path: '/portal/facility/general-info', title: 'General Info' },
                { path: '/portal/facility/water-body', title: 'Water Body' },
                { path: '/portal/facility/potw', title: 'POTW' },
                { path: '/portal/facility/offsite', title: 'Off-site Location' },
                { path: '/portal/facility/waste-treatment', title: 'Waste Treatment' }
            ]
        },
        { path: '/portal/root-chemicals', title: 'Root Chemicals',
            children: [
                { path: '/portal/root-chemicals/import-export', title: 'Import/Export' }
            ]
         },
        { path: '/portal/chemical-usage/bulk-entry', title: 'Chemical Usage',
            children: [
                { path: '/portal/chemical-usage/import-export', title: 'Import/Export' }
            ]
         },
        { path: '/portal/alternative-usage', title: 'Alternative Usage' },
        { path: '/portal/reports/threshold-report', title: 'Reports',
            children: [
                { path: '/portal/reports/threshold-report', title: 'Threshold Report' },
                { path: '/portal/reports/root-chemical-report', title: 'Root Chemical Report' },
            ]
        },
        { path: '/portal/forms', title: 'Forms' },
        { path: '/portal/supporting-documentation', title: 'Supporting Documentation',
            children: [
                { path: '/portal/supporting-documentation', title: 'Instructions' },
                { path: '/portal/uploaded-documents', title: 'Uploaded Documents' },
            ]
         },
        { path: '/portal/comment-log', title: 'Comment Log' },
    ];

    /**
     * Constructor
     */
    constructor(private facilityService: FacilityService,
                private commonDataService: CommonDataService,
                private subscriptionStatusService: SubscriptionStatusService,
                private router: Router
    )
    {
        
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    async ngOnInit(): Promise<void> {
        this.reportingPeriods = await this.commonDataService.getReportingPeriods();
        const [customers, facilityYear] = await firstValueFrom(
            combineLatest([
                this.facilityService.customers.pipe(filter(c => c.length > 0)),
                this.facilityService.facilityYears.pipe(
                    filter(fy => (fy?.customerId ?? 0) > 0 && (fy?.facilityId ?? 0) > 0 && (fy?.year ?? 0) > 0)
                )
            ]).pipe(take(1))
        );

        this.customers = customers;
        this.applyFacilityYearSelection(facilityYear);

        await this.updateSubscriptionStatus(facilityYear.customerId, facilityYear.facilityId ?? 0, facilityYear.year);

        this.facilityService.customers.subscribe(async customers => {
            this.reportingPeriods = await this.commonDataService.getReportingPeriods();
            this.customers = customers;

            if (this.latestFacilityYear) {
                this.applyFacilityYearSelection(this.latestFacilityYear);
            }
        });

        this.facilityService.facilityYears.subscribe(async facilityYear => {
            if ((facilityYear?.customerId ?? 0) <= 0 || (facilityYear?.facilityId ?? 0) <= 0 || (facilityYear?.year ?? 0) <= 0) {
                return;
            }

            this.applyFacilityYearSelection(facilityYear);
            
            await this.updateSubscriptionStatus(
                facilityYear.customerId,
                facilityYear.facilityId ?? 0,
                facilityYear.year
            );
            
            console.debug('Facility changed to', this.currentFacility, 'Year changed to', this.currentYear);
        });

        // Subscribe to subscription status changes
        this.subscriptionStatusService.subscriptionStatus$.subscribe(status => {
            this.subscriptionStatus = status;
        });

        // Track current route to hide overlay on purchase page
        this.router.events.subscribe(() => {
            this.isOnPurchasePage = this.router.url.includes('/portal/purchase');
            this.isOnCustomerDashboard = this.router.url.includes('/portal/dashboard');
        });
    }
    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    public onCustomerChange(customer: Customer): void {
        if( this.currentCustomer?.id === customer.id ) {
            return;
        }

        this.currentCustomer = customer;
        this.facilities = customer.facilities || [];
        this.currentFacility = this.facilities[0];
        this.changeFacilityYearSafely(this.currentCustomer.id, this.currentFacility.id, this.currentYear);
        this.setTitle();
    }

    public onFacilityChange(facility: Facility): void {
        this.currentFacility = facility;
        this.changeFacilityYearSafely(this.currentCustomer?.id || 0, facility.id, this.currentYear);
        this.setTitle();
        // Logic to handle facility switch can be added here
    }

    public onYearChange(year: number): void {
        this.currentYear = year;
        this.changeFacilityYearSafely(this.currentCustomer?.id || 0, this.currentFacility?.id || 0, this.currentYear);
        this.setTitle();
    }

    public retryFacilityLoad(): void {
        if (!this.requestedFacilityYear) {
            return;
        }

        const facilityId = this.requestedFacilityYear.facilityId ?? 0;
        if (facilityId <= 0) {
            return;
        }

        this.changeFacilityYearSafely(
            this.requestedFacilityYear.customerId,
            facilityId,
            this.requestedFacilityYear.year
        );
    }

    public navigateToPurchase(): void {
        this.router.navigate(['/portal/purchase']);
    }

    private async updateSubscriptionStatus(customerId: number, facilityId: number, year: number): Promise<void> {
        this.isSubscriptionStatusLoading = true;

        try {
            await this.subscriptionStatusService.checkSubscriptionStatus(customerId, facilityId, year);
        } finally {
            this.isSubscriptionStatusLoading = false;
        }
    }

    private changeFacilityYearSafely(customerId: number, facilityId: number, year: number): void {
        if (customerId <= 0 || facilityId <= 0 || year <= 0) {
            return;
        }

        this.requestedFacilityYear = { customerId, facilityId, year };
        this.isFacilitySwitchLoading = true;
        this.facilityLoadErrorMessage = null;

        this.facilityService.changeFacilityYear(customerId, facilityId, year)
            .catch(error => {
                console.error('Failed to change facility/year', { customerId, facilityId, year, error });
                this.facilityLoadErrorMessage = 'The selected facility could not be loaded. Please try again or select a different facility/year.';
            })
            .finally(() => {
                this.isFacilitySwitchLoading = false;
            });
    }

    private applyFacilityYearSelection(facilityYear: FacilityYear): void {
        this.facilityLoadErrorMessage = null;
        this.latestFacilityYear = facilityYear;
        this.currentCustomer = this.customers.find(c => c.id === facilityYear.customerId);
        this.currentReportingPeriods = this.currentCustomer?.reportingPeriods || [];
        this.facilities = this.currentCustomer?.facilities || [];
        this.currentFacility = this.facilities.find(f => f.id === facilityYear.facilityId);
        this.currentYear = facilityYear.year;
        this.updateFormsNavState();
        this.setTitle();
    }

    // Grey out (and add a tooltip to) the Forms nav item when the year has no Form R and no XML version configured.
    private updateFormsNavState(): void {
        const formsItem = this.navItems.find(item => item.path === '/portal/forms');
        if (!formsItem) {
            return;
        }

        const period = this.reportingPeriods.find(p => p.year === this.currentYear);
        const isConfigured = !!period?.formRVersion || !!period?.trimeWebVersion;

        formsItem.enabled = isConfigured;
        formsItem.tooltip = isConfigured ? undefined : `${this.currentYear} is not configured for forms`;
    }

    private setTitle() {
        this.title = this.currentFacility ? `${this.currentFacility.name} - ${this.currentYear}` : 'ComplyTRI';
    }
}